/**
 * STAGE Mascot - Empty State Component
 * DMV Soundstage's neutral platform guide
 * 
 * Usage: <StageEmptyState variant="tracks" />
 */

import { Link } from 'react-router-dom';
import { Button } from './ui/button';

// Official STAGE mascot image
const STAGE_IMAGE = "https://customer-assets.emergentagent.com/job_dmv-media-1/artifacts/4l63gu1k_C18FA1E7-13BE-4A8D-B43C-DA825C418C9F.png";

// Empty state configurations
const EMPTY_STATE_CONFIG = {
  // Charts - no data yet
  charts: {
    headline: "The charts are waiting.",
    hint: "Be the first artist to make the Top 20.",
    action: { label: "Join as Artist", href: "/signup?role=artist" }
  },
  
  // Artist profile - no tracks
  tracks: {
    headline: "No tracks here yet.",
    hint: "Upload your first track to get discovered.",
    action: { label: "Upload Music", href: "/dashboard?tab=music" }
  },
  
  // Video section - no uploads
  videos: {
    headline: "No videos uploaded yet.",
    hint: "Link your first music video to your DSP track.",
    action: { label: "Add Video", href: "/dashboard?tab=music" }
  },
  
  // Events page - no events
  events: {
    headline: "No events scheduled.",
    hint: "Check back soon for DMV shows and events.",
    action: null
  },
  
  // Battles page
  battles: {
    headline: "Cypher battles coming soon.",
    hint: "The stage is being prepared for lyrical competition.",
    action: null
  },
  
  // Studio directory - empty
  studios: {
    headline: "No studios listed yet.",
    hint: "List your studio to connect with DMV artists.",
    action: { label: "List Your Studio", href: "/signup?role=studio" }
  },
  
  // Search results - no matches
  search: {
    headline: "No results found.",
    hint: "Try a different search term or browse the charts.",
    action: { label: "View Charts", href: "/charts" }
  },
  
  // New user onboarding
  onboarding: {
    headline: "Welcome to DMV Soundstage.",
    hint: "The platform where local artists take center stage.",
    action: { label: "Get Started", href: "/select-role" }
  },
  
  // Interviews - none yet
  interviews: {
    headline: "No interviews yet.",
    hint: "Be the first to share your story on DMV Soundstage.",
    action: null
  },
  
  // Creatives directory
  creatives: {
    headline: "No creatives listed yet.",
    hint: "Join the community as a podcaster, videographer, or engineer.",
    action: { label: "Apply Now", href: "/dashboard?tab=roles" }
  },
  
  // Podcasters
  podcasters: {
    headline: "No podcasters yet.",
    hint: "Be the first podcaster to join DMV Soundstage.",
    action: { label: "Join as Podcaster", href: "/signup?role=podcaster" }
  },
  
  // Following/empty feed
  feed: {
    headline: "Nothing playing yet.",
    hint: "Follow artists to see their latest releases here.",
    action: { label: "Discover Artists", href: "/artists" }
  },
  
  // Generic fallback
  default: {
    headline: "This stage is waiting.",
    hint: "The spotlight is open.",
    action: null
  }
};

/**
 * StageEmptyState Component
 * 
 * @param {string} variant - Type of empty state (tracks, videos, events, etc.)
 * @param {string} customHeadline - Override default headline
 * @param {string} customHint - Override default hint
 * @param {object} customAction - Override default action { label, href }
 * @param {string} size - sm, md, lg (affects image and text size)
 * @param {string} className - Additional CSS classes
 */
export function StageEmptyState({ 
  variant = 'default',
  customHeadline,
  customHint,
  customAction,
  size = 'md',
  className = ''
}) {
  const config = EMPTY_STATE_CONFIG[variant] || EMPTY_STATE_CONFIG.default;
  
  const headline = customHeadline || config.headline;
  const hint = customHint || config.hint;
  const action = customAction !== undefined ? customAction : config.action;

  const sizeClasses = {
    sm: {
      container: 'py-8',
      image: 'w-24 h-24',
      headline: 'text-lg',
      hint: 'text-sm'
    },
    md: {
      container: 'py-12',
      image: 'w-32 h-32',
      headline: 'text-xl',
      hint: 'text-base'
    },
    lg: {
      container: 'py-16',
      image: 'w-40 h-40',
      headline: 'text-2xl',
      hint: 'text-lg'
    }
  };

  const sizes = sizeClasses[size] || sizeClasses.md;

  return (
    <div 
      className={`flex flex-col items-center justify-center text-center ${sizes.container} ${className}`}
      data-testid={`stage-empty-${variant}`}
    >
      {/* Official STAGE Mascot Image */}
      <div className={`${sizes.image} mb-6`}>
        <img 
          src={STAGE_IMAGE} 
          alt="STAGE mascot" 
          className="w-full h-full object-contain"
          loading="lazy"
        />
      </div>

      {/* Headline */}
      <h3 className={`font-heading font-bold ${sizes.headline} text-foreground mb-2`}>
        {headline}
      </h3>

      {/* Hint */}
      <p className={`${sizes.hint} text-muted-foreground max-w-sm mb-6`}>
        {hint}
      </p>

      {/* Action Button (optional) */}
      {action && (
        <Link to={action.href}>
          <Button variant="outline" className="border-secondary/50 hover:border-secondary hover:bg-secondary/10">
            {action.label}
          </Button>
        </Link>
      )}
    </div>
  );
}

/**
 * Compact version for inline empty states
 */
export function StageEmptyStateCompact({ 
  variant = 'default',
  customHint,
  className = ''
}) {
  const config = EMPTY_STATE_CONFIG[variant] || EMPTY_STATE_CONFIG.default;
  const hint = customHint || config.hint;

  return (
    <div className={`flex items-center gap-4 p-4 ${className}`}>
      <img 
        src={STAGE_IMAGE} 
        alt="STAGE" 
        className="w-16 h-16 object-contain"
        loading="lazy"
      />
      <p className="text-sm text-muted-foreground">{hint}</p>
    </div>
  );
}

export default StageEmptyState;
